/**
 * BOEI chat widget — framework-agnostic loader for the official BOEI embed.
 *
 * Lives in seminargo-shared (synced as .ts only, so it must not use JSX/React)
 * and is consumed by both seminargo-client-platform and seminargo-client-backend.
 * Replaces the previous Smartsupp live-chat integration.
 *
 * We load BOEI's official embed script (not a raw iframe), so the floating bubble
 * renders natively with working open/close. The key is the BOEI widget embed key
 * (from the dashboard Install tab) — NOT the bot id.
 */

export const BOEI_EMBED_KEY = '179dddcb-e757-4588-9b3b-d2e2c52b260a';
export const BOEI_LOADER_SRC = `https://app.boei.help/embed/k/${BOEI_EMBED_KEY}`;

const LOADER_ID = 'boei-embed-loader';

// Element ids the BOEI embed injects into the document; removed on teardown.
const BOEI_NODE_IDS = [LOADER_ID, 'boei-script', 'boei_button'];

/**
 * Loads the official BOEI embed script, which renders the floating chat bubble
 * with native open/close. Idempotent: a no-op if the loader is already present
 * (safe to call from React effects / re-renders).
 */
export function mountBoeiChat(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(LOADER_ID)) return;

  const script = document.createElement('script');
  script.id = LOADER_ID;
  script.src = BOEI_LOADER_SRC;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Fully removes the BOEI widget from the page (loader, injected button script,
 * the button and any chat containers). Safe to call when not mounted. Used to
 * hide the widget when the backoffice leaves EPILO mode.
 */
export function unmountBoeiChat(): void {
  if (typeof document === 'undefined') return;

  // Use the native API to hide the button if BOEI has finished loading.
  const boeiEmbed = (window as unknown as { boeiEmbed?: { hideBoeiButton?: () => void } }).boeiEmbed;
  if (boeiEmbed && typeof boeiEmbed.hideBoeiButton === 'function') {
    try {
      boeiEmbed.hideBoeiButton();
    } catch {
      /* ignore — fall through to DOM cleanup */
    }
  }

  // Remove the scripts and any leftover BOEI DOM (button, chat window, overlays)
  // so the widget disappears and can be cleanly re-mounted later.
  BOEI_NODE_IDS.forEach((id) => document.getElementById(id)?.remove());
  document.querySelectorAll('[id^="boei"], [class^="boei"]').forEach((el) => el.remove());
}
